import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { VisibilityPredicateType } from "../../../registry/visibility";
import { entityOfType } from "../../../utils/system-entities";
import { IntegrationKey, SystemEntity } from "@erp/shared-types";

/**
 * Ingest Sales Load from Famous Step
 *
 * Fetches one or more sales loads from Famous Software by load IDs and ingests all
 * associated sales orders into our database. The load metadata is stored
 * on each sales order's originMetadata.salesLoad field for later use
 * in charge allocation (e.g., matching freight invoices to load orders).
 *
 * This step:
 * - Connects to Famous Oracle database
 * - Retrieves load data with associated order numbers for each load
 * - Ingests all sales orders belonging to the loads
 * - Updates each sales order with load metadata (loadId, loadNumber, etc.)
 *
 * Input: `loadIds` (array of Famous load reference numbers) must be provided at runtime.
 *
 * Note: This is a backend-only step that requires Famous database access.
 */
export const ingestSalesLoadStep = defineStepConfig({
  type: "ingest_sales_load",
  name: "Ingest Sales Load from Famous",
  description:
    "Fetch and ingest all sales orders from a Famous sales load, with load metadata",
  category: "Integration",
  libraryModule: "Famous",
  visibility: [
    [
      {
        type: VisibilityPredicateType.Integration,
        integration: IntegrationKey.Famous,
      },
    ],
  ],
  inputSchema: z.object({
    loadIds: z
      .array(z.string().trim())
      .describe(
        "Array of Famous sales load reference numbers (ARLOADID). Resolved to internal IDs at runtime.",
      ),
  }),

  outputSchema: z.object({
    salesOrders: z
      .array(entityOfType(SystemEntity.AR_SALES_ORDER))
      .describe("Array of Sales Orders ingested from all loads")
      .optional(),
    transferOrders: z
      .array(entityOfType(SystemEntity.IM_TRANSFER_ORDER))
      .describe("Array of Transfer Orders ingested from all loads")
      .optional(),
    shipments: z
      .array(entityOfType(SystemEntity.SCM_SHIPMENT))
      .describe("Array of Shipments created for each load")
      .optional(),
  }),

  configSchema: z.object({
    cancelIfNotFound: z
      .boolean()
      .default(false)
      .describe("Cancel the workflow if the load is not found"),
    cancelIfEmpty: z
      .boolean()
      .default(false)
      .describe("Cancel the workflow if the load has no orders"),
  }),

  emits: [] as const,
});
